import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import {
  REMEMBER_ME_STORAGE_KEY,
  persistRememberMe,
  getRememberMeFromStorage,
} from '@utils/authRememberMe'

export interface User {
  id: string
  email: string
  username: string
  firstName?: string
  lastName?: string
  profileImageUrl?: string
  subscriptionTier: 'free' | 'premium' | 'pro'
  role?: 'user' | 'admin' | 'super_admin' | 'moderator'
  createdAt: string
  updatedAt: string
}

export interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  rememberMe: boolean
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
}

const getStorageItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function clearAuthStorage(): void {
  try {
    localStorage.removeItem('token')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem(REMEMBER_ME_STORAGE_KEY)
  } catch {
    // ignore storage errors
  }
}

const isTokenExpired = (token: string): boolean => {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return true
    const payload = JSON.parse(atob(parts[1]))
    if (typeof payload.exp !== 'number') return false
    return payload.exp < Date.now() / 1000
  } catch {
    return true
  }
}

const rawStoredToken = getStorageItem('token')

// If the stored access token is expired, clear it from localStorage so the
// AuthInitializer can fall through to the refresh-token flow (or reject outright).
let storedToken: string | null = rawStoredToken
if (rawStoredToken && isTokenExpired(rawStoredToken)) {
  try {
    localStorage.removeItem('token')
  } catch {
    // ignore storage errors
  }
  storedToken = null
}

const initialRememberMe = getRememberMeFromStorage()

const initialState: AuthState = {
  user: null,
  token: storedToken,
  refreshToken: null,
  rememberMe: initialRememberMe,
  isAuthenticated: false,
  isLoading: true,
  error: null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    loginStart: (state) => {
      state.isLoading = true
      state.error = null
    },
    loginSuccess: (state, action: PayloadAction<{ user: User; token: string; refreshToken?: string; rememberMe?: boolean }>) => {
      state.isLoading = false
      state.user = action.payload.user
      state.token = action.payload.token
      state.refreshToken = action.payload.refreshToken ?? null
      state.rememberMe = action.payload.rememberMe !== undefined
        ? action.payload.rememberMe
        : state.rememberMe
      state.isAuthenticated = true
      state.error = null

      localStorage.setItem('token', action.payload.token)
      persistRememberMe(state.rememberMe)
    },
    loginFailure: (state, action: PayloadAction<string>) => {
      state.isLoading = false
      state.error = action.payload
      state.isAuthenticated = false
    },
    logout: (state) => {
      state.user = null
      state.token = null
      state.refreshToken = null
      state.rememberMe = false
      state.isAuthenticated = false
      state.isLoading = false
      state.error = null
      clearAuthStorage()
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      }
    },
    tokenValidated: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      state.isAuthenticated = true
      state.isLoading = false
    },
    tokenInvalid: (state) => {
      state.user = null
      state.token = null
      state.refreshToken = null
      state.rememberMe = false
      state.isAuthenticated = false
      state.isLoading = false
      clearAuthStorage()
    },
    clearError: (state) => {
      state.error = null
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    syncWithLocalStorage: (state) => {
      const token = localStorage.getItem('token')

      if (token) {
        state.token = token
        state.refreshToken = null
        state.isAuthenticated = true
      } else {
        state.token = null
        state.refreshToken = null
        state.isAuthenticated = false
      }
    }
  },
})

export const {
  loginStart,
  loginSuccess,
  loginFailure,
  logout,
  updateUser,
  tokenValidated,
  tokenInvalid,
  clearError,
  setLoading,
  syncWithLocalStorage
} = authSlice.actions

export default authSlice.reducer
